/**
 * Instance Detail Component
 *
 * Displays detailed information for a single VM instance with tabs for:
 * - Overview (specs, status, metadata)
 * - Monitoring (CPU/Memory charts - placeholder)
 */

import SubHeaders from '@/components/Headers/SubHeaders'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { getInstance } from '../../../services/capacity'
import { paths } from '@/utils/path'
import { useParams } from 'react-router-dom'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { GridInformation, Tabs } from 'vnetwork-ui'
import { Badge } from '@/components/ui/badge'
import useTabs from '@/hooks/useTabs'
import { tabs } from './tabs'

/** Get status badge props */
const getStatusBadge = (status: string) => {
    switch (status?.toLowerCase()) {
        case 'running':
            return {
                variant: 'default' as const,
                className: 'bg-green-100 text-green-700 border-green-200',
                label: 'Running',
            }
        case 'stopped':
            return {
                variant: 'secondary' as const,
                className: 'bg-gray-100 text-gray-700 border-gray-200',
                label: 'Stopped',
            }
        case 'suspended':
            return {
                variant: 'outline' as const,
                className: 'bg-amber-100 text-amber-700 border-amber-200',
                label: 'Suspended',
            }
        case 'starting':
        case 'stopping':
            return {
                variant: 'outline' as const,
                className: 'bg-blue-100 text-blue-700 border-blue-200',
                label: status.charAt(0).toUpperCase() + status.slice(1),
            }
        case 'error':
            return {
                variant: 'destructive' as const,
                className: 'bg-red-100 text-red-700 border-red-200',
                label: 'Error',
            }
        default:
            return {
                variant: 'outline' as const,
                className: '',
                label: status || 'Unknown',
            }
    }
}

const InstanceDetail = () => {
    const { instanceId } = useParams<{ instanceId: string }>()

    const { data: instance, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.INSTANCE_DETAIL, instanceId],
        queryFn: () => getInstance(instanceId!),
    })

    const { TabComponent, TabProps } = useTabs({ tabs: tabs(instance) })

    // Render loading/error state at the end (after all hooks)
    if (renderState()) return renderState()

    const statusBadge = getStatusBadge(instance?.status || '')
    const memoryGB = instance?.spec?.memory_mb
        ? (instance.spec.memory_mb / 1024).toFixed(1)
        : '-'

    return (
        <div>
            <SubHeaders
                actions={[]}
                breadcrumbs={[
                    {
                        path: paths.warehouse,
                        title: 'Capacities & Resources',
                    },
                    {
                        path: `${paths.warehouse}?tab=cloud`,
                        title: 'Cloud Resources',
                    },
                    {
                        path: instance?.zone_id
                            ? `/warehouse/cloud/zones/${instance.zone_id}`
                            : `${paths.warehouse}?tab=cloud`,
                        title: instance?.zone?.zone_name || 'Zone',
                    },
                    {
                        path: '',
                        title: instance?.name || '',
                    },
                ]}
            />
            <GridInformation
                data={[
                    {
                        label: 'Instance Name',
                        value: instance?.name || '-',
                    },
                    {
                        label: 'UID',
                        value: instance?.uid || '-',
                    },
                    {
                        label: 'Status',
                        value: (
                            <Badge
                                variant={statusBadge.variant}
                                className={statusBadge.className}
                            >
                                {statusBadge.label}
                            </Badge>
                        ),
                    },
                    {
                        label: 'Zone',
                        value: instance?.zone?.zone_name || '-',
                    },
                    {
                        label: 'Provider',
                        value: instance?.zone?.provider?.name || '-',
                    },
                    {
                        label: 'Zone Type',
                        value: (
                            <Badge variant="outline">
                                {instance?.zone_type?.toUpperCase() || ''}
                            </Badge>
                        ),
                    },
                    {
                        label: 'CPU Cores',
                        value: instance?.spec?.cpu?.toString() || '-',
                    },
                    {
                        label: 'Memory',
                        value: memoryGB !== '-' ? `${memoryGB} GB` : '-',
                    },
                    {
                        label: 'Disk',
                        value: instance?.spec?.disk_gb
                            ? `${instance.spec.disk_gb} GB`
                            : '-',
                    },
                    {
                        label: 'Last Synced',
                        value: instance?.synchronized_at
                            ? new Date(instance.synchronized_at).toLocaleString(
                                  'en-US',
                                  {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit',
                                  }
                              )
                            : '-',
                    },
                    {
                        label: 'Created At',
                        value: instance?.created_at
                            ? new Date(instance.created_at).toLocaleString(
                                  'en-US',
                                  {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric',
                                  }
                              )
                            : '-',
                    },
                    {
                        label: 'Description',
                        value: instance?.description || '-',
                        className: 'lg:col-span-2',
                        hidden: !instance?.description,
                    },
                ]}
                head={{
                    title: 'Instance Information',
                }}
            />

            <Tabs {...TabProps} />

            {TabComponent}
        </div>
    )
}

export default InstanceDetail
